import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Clock, MapPin } from "lucide-react";
import { claimContractorShift, requestContractorReplacement } from "@/lib/contractors.functions";
import { contractedShiftStatus, shiftHours } from "@/lib/contractors";
import { ShiftStatusBadge } from "@/components/contractors/ShiftStatusBadge";
import { useContractorWorkspace } from "./contractor.index";

export const Route = createFileRoute("/_authenticated/contractor/shift-claim")({
  component: ShiftClaimPage,
  head: () => ({
    meta: [
      { title: "Shift Claim — MissionReady" },
      { name: "description", content: "Claim contracted shifts at your assigned sites, first come first served." },
      { property: "og:title", content: "Shift Claim — MissionReady" },
      { property: "og:description", content: "Unclaimed and claimed contracted shifts for your contractor organization." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function when(s: { starts_at: string; ends_at: string }) {
  const start = new Date(s.starts_at);
  const end = new Date(s.ends_at);
  return `${start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} – ${end.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function ShiftClaimPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error } = useContractorWorkspace();
  const claimFn = useServerFn(claimContractorShift);
  const replaceFn = useServerFn(requestContractorReplacement);
  const [busy, setBusy] = useState<string | null>(null);

  const shifts: any[] = data?.shifts ?? [];
  const sites: any[] = data?.sites ?? [];
  const teammates: any[] = data?.teammates ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["contractor-workspace", user?.id ?? "anon"] });

  const unclaimed = shifts.filter((s) => !s.officer_id && s.status !== "cancelled");
  const claimed = shifts.filter((s) => s.officer_id && s.status !== "cancelled");

  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? "Assigned site";
  const memberName = (id: string) => {
    if (id === user?.id) return "You";
    const t = teammates.find((x) => x.user_id === id);
    return t?.profile?.full_name ?? t?.profile?.email ?? "Contractor member";
  };

  const claim = async (id: string) => {
    setBusy(id);
    try {
      await claimFn({ data: { shiftId: id } });
      toast.success("Shift claimed");
      await refresh();
    } catch (err: any) {
      const msg = typeof err?.message === "string" ? err.message : "Could not claim this shift";
      toast.error(msg.includes("already been claimed") ? "Someone claimed that shift first" : msg);
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  const findReplacement = async (id: string) => {
    if (typeof window !== "undefined" &&
      !window.confirm("Return this shift to the claim board so someone else can pick it up?")) return;
    setBusy(id);
    try {
      await replaceFn({ data: { shiftId: id } });
      toast.success("Replacement requested — the shift is back on the board");
      await refresh();
    } catch (err: any) {
      toast.error(typeof err?.message === "string" ? err.message : "Could not request a replacement");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:pt-6">
      <Link to="/contractor" className="mb-3 inline-flex min-h-10 items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Contracted work
      </Link>
      <h1 className="font-display text-2xl font-semibold">Shift Claim</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        First come, first served. You can only claim shifts at sites your organization is assigned to.
      </p>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading the board…</p>
      ) : error ? (
        <p className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Data source unavailable. Try again shortly.
        </p>
      ) : (
        <>
          <section className="mt-6" aria-labelledby="unclaimed-heading">
            <h2 id="unclaimed-heading" className="font-display text-lg font-semibold">
              Unclaimed <span className="text-sm font-normal text-muted-foreground">({unclaimed.length})</span>
            </h2>
            <ul className="mt-3 space-y-3">
              {unclaimed.length === 0 ? (
                <li className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No shifts available to claim right now.
                </li>
              ) : unclaimed.map((s) => (
                <li key={s.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-sm font-semibold">
                        <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden /> {siteName(s.site_id)}
                      </p>
                      <p className="mt-1 text-sm">{when(s)}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden /> {shiftHours(s.starts_at, s.ends_at)} hours
                        {s.role_label ? ` · ${s.role_label}` : ""}
                      </p>
                      {s.notes ? <p className="mt-1.5 text-xs text-muted-foreground">{s.notes}</p> : null}
                    </div>
                    <ShiftStatusBadge status={contractedShiftStatus(s)} />
                  </div>
                  <Button className="mt-3 min-h-12 w-full" disabled={busy === s.id} onClick={() => claim(s.id)}>
                    {busy === s.id ? "Claiming…" : "Claim"}
                  </Button>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-8" aria-labelledby="claimed-heading">
            <h2 id="claimed-heading" className="font-display text-lg font-semibold">
              Claimed <span className="text-sm font-normal text-muted-foreground">({claimed.length})</span>
            </h2>
            <ul className="mt-3 space-y-3">
              {claimed.length === 0 ? (
                <li className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  Nothing claimed yet.
                </li>
              ) : claimed.map((s) => (
                <li key={s.id} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-sm font-semibold">
                        <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden /> {siteName(s.site_id)}
                      </p>
                      <p className="mt-1 text-sm">{when(s)}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Claimed by {memberName(s.officer_id)} · {shiftHours(s.starts_at, s.ends_at)} hours
                      </p>
                    </div>
                    <ShiftStatusBadge status={contractedShiftStatus(s)} />
                  </div>
                  {s.officer_id === user?.id ? (
                    <Button variant="outline" className="mt-3 min-h-12 w-full" disabled={busy === s.id}
                      onClick={() => findReplacement(s.id)}>
                      Find replacement
                    </Button>
                  ) : (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Only the person who claimed a shift can ask for a replacement.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
